/** 风控两屏（risk / riskreport）取数与解析（G3，#4604）。
 *
 * 形状以后端为准：
 * - `GET /risk/limits` → `{path, digest, limits:{instrument, strategy, portfolio, override}}`（`config/risk_limits.json` 原样）；
 * - `GET /risk/report?strategy=&date=` → `RiskGate.daily_report` 的产物（`var/paper/<sid>/<date>/risk.json`），
 *   三层固定取 `instrument` / `strategy` / `portfolio` 三个键；**没有** `breaches` 键（日报的点名永不触发，见屏规格待裁决）；
 * - `GET /risk/overrides` → `{overrides:[{scope, operator, reason, granted_utc, expires_utc, active?}]}`，
 *   `active` 是待加字段：后端不给时记 `null`，界面标「缺」，前端不自己比时间。
 * 组合层是跨策略汇总口径（#4749）：`/risk/limits` 带 `portfolio_scope{scope, strategies}`，
 * `/risk/report` 的 `portfolio` 段带 `scope` / `participants` / `missing`；旧归档 `scope=legacy_single_strategy`。
 * 界面照后端给的参与策略显示，不把各策略组合层数字相加冒充汇总。
 */

import {
  apiUrl,
  expectList,
  expectRecord,
  optionalNumber,
  optionalString,
  query,
  requireBoolean,
  requireNumber,
  requireString,
} from "../../api/client";
import { ApiError } from "../../api/errors";

/** 组合层口径：`portfolio` = 跨策略汇总；`legacy_single_strategy` = #4749 之前的旧归档。 */
export interface PortfolioScope {
  scope: string | null;
  participants: { strategy_id: string; trade_date: string | null; starting_cash: number | null; halted: boolean | null }[];
  missing: { strategy_id: string; reason: string }[];
}

/** 组合层一行的口径说明：参与策略与缺的策略照后端列出。 */
export function portfolioScopeLabel(scope: PortfolioScope | null): string {
  if (!scope || scope.scope === null) return "口径缺（后端未给 scope）";
  if (scope.scope === "legacy_single_strategy") return "旧归档：单策略口径";
  const ids = scope.participants.map((p) => p.strategy_id);
  const missing = scope.missing.map((m) => m.strategy_id);
  return `跨策略汇总：${ids.length ? ids.join("、") : "无"}${missing.length ? `（缺 ${missing.join("、")}）` : ""}`;
}

export interface RiskLimitsPayload {
  path: string;
  digest: string;
  /** 组合层作用范围：`scope=portfolio` 与在跑策略；后端不给时为 `null`。 */
  portfolio_scope: { scope: string; strategies: string[] } | null;
  instrument: { max_weight: number; max_loss_pct: number; on_breach: string };
  strategy: { max_gross_weight: number; max_loss_pct: number; on_breach: string; reduce_to: number | null };
  portfolio: { max_gross_weight: number; max_drawdown: number; on_breach: string };
  override: { max_ttl_hours: number };
}

export function parseRiskLimits(body: unknown): RiskLimitsPayload {
  const url = apiUrl("/risk/limits");
  const root = expectRecord(body, url, "风险限额");
  const limits = expectRecord(root["limits"], url, "limits");
  const ins = expectRecord(limits["instrument"], url, "limits.instrument");
  const st = expectRecord(limits["strategy"], url, "limits.strategy");
  const pf = expectRecord(limits["portfolio"], url, "limits.portfolio");
  const ov = expectRecord(limits["override"], url, "limits.override");
  const ps = root["portfolio_scope"];
  let portfolioScope: RiskLimitsPayload["portfolio_scope"] = null;
  if (ps !== undefined && ps !== null) {
    const r = expectRecord(ps, url, "portfolio_scope");
    const strategies = r["strategies"];
    portfolioScope = {
      scope: requireString(r, "scope", url),
      strategies: Array.isArray(strategies) ? strategies.filter((x): x is string => typeof x === "string") : [],
    };
  }
  return {
    path: requireString(root, "path", url),
    digest: requireString(root, "digest", url),
    portfolio_scope: portfolioScope,
    instrument: { max_weight: requireNumber(ins, "max_weight", url), max_loss_pct: requireNumber(ins, "max_loss_pct", url), on_breach: requireString(ins, "on_breach", url) },
    strategy: {
      max_gross_weight: requireNumber(st, "max_gross_weight", url),
      max_loss_pct: requireNumber(st, "max_loss_pct", url),
      on_breach: requireString(st, "on_breach", url),
      reduce_to: optionalNumber(st, "reduce_to"),
    },
    portfolio: { max_gross_weight: requireNumber(pf, "max_gross_weight", url), max_drawdown: requireNumber(pf, "max_drawdown", url), on_breach: requireString(pf, "on_breach", url) },
    override: { max_ttl_hours: requireNumber(ov, "max_ttl_hours", url) },
  };
}

export type Layer = "instrument" | "strategy" | "portfolio";
export const LAYERS: Layer[] = ["instrument", "strategy", "portfolio"];
export const LAYER_LABEL: Record<Layer, string> = { instrument: "标的", strategy: "策略", portfolio: "组合" };

export interface PeakBar {
  ts_event: number;
  value: number;
  utilization: number;
  drawdown: number | null;
}

export interface LayerReport {
  limit: number;
  /** 标的 / 策略层是 `loss_limit`，组合层是 `drawdown_limit`。 */
  loss_limit: number;
  peak_utilization: number;
  breached: boolean;
  peak_bar: PeakBar | null;
  bars: Record<string, unknown>[];
}

export interface RiskAction {
  layer: string;
  action: string;
  reason: string;
  value: number | null;
  limit: number | null;
  ts_event: number | null;
  scope: string | null;
  operator: string | null;
  original_quantity: number | null;
  adjusted_quantity: number | null;
}

export interface RiskRejection { instrument_id: string; layer: string; reason: string; ts_event: number }

export interface OverrideRecord {
  scope: string;
  operator: string;
  reason: string;
  granted_utc: string;
  expires_utc: string;
  /** 待加字段：后端不给时为 `null`（界面标「缺」）。 */
  active: boolean | null;
}

export interface RiskDailyReport {
  as_of_ts: number;
  strategy_id: string;
  limits_digest: string;
  layers: Record<Layer, LayerReport>;
  /** 组合层口径与参与策略（`portfolio.scope` / `participants` / `missing`）。 */
  portfolio_scope: PortfolioScope;
  rejections: RiskRejection[];
  actions: RiskAction[];
  overrides: OverrideRecord[];
}

function parsePeak(value: unknown, url: string): PeakBar | null {
  if (value === null || value === undefined) return null;
  const r = expectRecord(value, url, "evidence.peak_bar");
  return { ts_event: requireNumber(r, "ts_event", url), value: requireNumber(r, "value", url), utilization: requireNumber(r, "utilization", url), drawdown: optionalNumber(r, "drawdown") };
}

function parseLayer(root: Record<string, unknown>, layer: Layer, url: string): LayerReport {
  const r = expectRecord(root[layer], url, `风险报告 ${layer}`);
  const evidence = expectRecord(r["evidence"], url, `${layer}.evidence`);
  return {
    limit: requireNumber(r, "limit", url),
    loss_limit: requireNumber(r, layer === "portfolio" ? "drawdown_limit" : "loss_limit", url),
    peak_utilization: requireNumber(r, "peak_utilization", url),
    breached: requireBoolean(r, "breached", url),
    peak_bar: parsePeak(evidence["peak_bar"], url),
    bars: expectList(evidence, "bars", url, `${layer}.evidence.bars`),
  };
}

export function parseOverride(value: unknown, url: string): OverrideRecord {
  const r = expectRecord(value, url, "放行记录");
  return {
    scope: requireString(r, "scope", url),
    operator: requireString(r, "operator", url),
    reason: requireString(r, "reason", url),
    granted_utc: requireString(r, "granted_utc", url),
    expires_utc: requireString(r, "expires_utc", url),
    active: typeof r["active"] === "boolean" ? (r["active"] as boolean) : null,
  };
}

function parsePortfolioScope(root: Record<string, unknown>, url: string): PortfolioScope {
  const pf = expectRecord(root["portfolio"], url, "风险报告 portfolio");
  const list = (key: string): Record<string, unknown>[] =>
    Array.isArray(pf[key]) ? (pf[key] as unknown[]).filter((x): x is Record<string, unknown> => typeof x === "object" && x !== null) : [];
  return {
    scope: optionalString(pf, "scope"),
    participants: list("participants").map((r) => ({
      strategy_id: requireString(r, "strategy_id", url),
      trade_date: optionalString(r, "trade_date"),
      starting_cash: optionalNumber(r, "starting_cash"),
      halted: typeof r["halted"] === "boolean" ? (r["halted"] as boolean) : null,
    })),
    missing: list("missing").map((r) => ({ strategy_id: requireString(r, "strategy_id", url), reason: optionalString(r, "reason") ?? "" })),
  };
}

export function parseRiskDailyReport(body: unknown): RiskDailyReport {
  const url = apiUrl("/risk/report");
  const root = expectRecord(body, url, "风险报告");
  return {
    as_of_ts: requireNumber(root, "as_of_ts", url),
    strategy_id: requireString(root, "strategy_id", url),
    limits_digest: requireString(root, "limits_digest", url),
    layers: {
      instrument: parseLayer(root, "instrument", url),
      strategy: parseLayer(root, "strategy", url),
      portfolio: parseLayer(root, "portfolio", url),
    },
    portfolio_scope: parsePortfolioScope(root, url),
    rejections: expectList(root, "rejections", url, "rejections").map((r) => ({
      instrument_id: requireString(r, "instrument_id", url),
      layer: requireString(r, "layer", url),
      reason: requireString(r, "reason", url),
      ts_event: requireNumber(r, "ts_event", url),
    })),
    actions: expectList(root, "actions", url, "actions").map((r) => ({
      layer: requireString(r, "layer", url),
      action: requireString(r, "action", url),
      reason: optionalString(r, "reason") ?? "",
      value: optionalNumber(r, "value"),
      limit: optionalNumber(r, "limit"),
      ts_event: optionalNumber(r, "ts_event"),
      scope: optionalString(r, "scope"),
      operator: optionalString(r, "operator"),
      original_quantity: optionalNumber(r, "original_quantity"),
      adjusted_quantity: optionalNumber(r, "adjusted_quantity"),
    })),
    overrides: expectList(root, "overrides", url, "overrides").map((r) => parseOverride(r, url)),
  };
}

export function parseOverrides(body: unknown): OverrideRecord[] {
  const url = apiUrl("/risk/overrides");
  return expectList(body, "overrides", url, "放行记录").map((r) => parseOverride(r, url));
}

/** 日终占用：取各层 `evidence.bars` 最后一条（屏规格「当前占用」）。 */
export function endOfDay(report: RiskDailyReport): Record<Layer, number | null> {
  const last = (layer: Layer): Record<string, unknown> | null => {
    const bars = report.layers[layer].bars;
    return bars[bars.length - 1] ?? null;
  };
  const ins = last("instrument");
  let insUtil: number | null = null;
  if (ins && typeof ins["instrument"] === "object" && ins["instrument"] !== null) {
    const values = Object.values(ins["instrument"] as Record<string, { utilization?: unknown }>)
      .map((item) => (typeof item?.utilization === "number" ? item.utilization : null))
      .filter((x): x is number => x !== null);
    insUtil = values.length ? Math.max(...values) : 0;
  }
  const util = (row: Record<string, unknown> | null): number | null => (row && typeof row["utilization"] === "number" ? (row["utilization"] as number) : null);
  return { instrument: insUtil, strategy: util(last("strategy")), portfolio: util(last("portfolio")) };
}

/** 标的层逐 bar 按标的展开（配对两腿各占一行，不合并）。 */
export function instrumentRows(report: RiskDailyReport): { ts_event: number; instrument_id: string; gross_weight: number; utilization: number }[] {
  const out: { ts_event: number; instrument_id: string; gross_weight: number; utilization: number }[] = [];
  for (const bar of report.layers.instrument.bars) {
    const tsEvent = typeof bar["ts_event"] === "number" ? (bar["ts_event"] as number) : 0;
    const per = bar["instrument"];
    if (typeof per !== "object" || per === null) continue;
    for (const [id, item] of Object.entries(per as Record<string, Record<string, unknown>>)) {
      out.push({
        ts_event: tsEvent,
        instrument_id: id,
        gross_weight: typeof item?.["gross_weight"] === "number" ? (item["gross_weight"] as number) : NaN,
        utilization: typeof item?.["utilization"] === "number" ? (item["utilization"] as number) : NaN,
      });
    }
  }
  return out;
}

export const readRiskLimits = (): Promise<RiskLimitsPayload> => query<unknown>("/risk/limits").then(parseRiskLimits);
export const readOverrides = (): Promise<OverrideRecord[]> => query<unknown>("/risk/overrides").then(parseOverrides);

/** 单策略单日风险报告；404 → `{missing: detail}`，与「报告损坏（500）」分开。 */
export async function readRiskDailyReport(strategy: string, date: string): Promise<{ report: RiskDailyReport } | { missing: string }> {
  try {
    return { report: parseRiskDailyReport(await query<unknown>("/risk/report", { strategy, date })) };
  } catch (error) {
    if (error instanceof ApiError && error.kind === "http" && error.status === 404) return { missing: error.message };
    throw error;
  }
}

export interface PaperRun {
  run_id: string;
  trade_date: string | null;
  strategy_id: string | null;
  risk: string | null;
  status: string | null;
  halted: boolean | null;
}

/** 纸上盘运行索引（只取风控两屏要的字段，其余字段不在这里校验）。 */
export function parsePaperRuns(body: unknown): PaperRun[] {
  const url = apiUrl("/runs");
  return expectList(body, "runs", url, "运行索引").map((r) => ({
    run_id: requireString(r, "run_id", url),
    trade_date: optionalString(r, "trade_date"),
    strategy_id: optionalString(r, "strategy_id"),
    risk: optionalString(r, "risk"),
    status: optionalString(r, "status"),
    halted: typeof r["halted"] === "boolean" ? (r["halted"] as boolean) : null,
  }));
}

export const readPaperRuns = (strategy?: string): Promise<PaperRun[]> =>
  query<unknown>("/runs", { mode: "纸上盘", strategy }).then(parsePaperRuns);

export function scopeLooksValid(scope: string): boolean {
  return /^(instrument|strategy|portfolio)$/.test(scope) || /^(instrument|strategy):\S+$/.test(scope);
}
